import { useEffect, useState, useRef } from 'react';
import { createStyles } from '@mantine/core';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Confetti from 'react-confetti';
import Trans from 'next-translate/Trans';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { IconCircleCheck } from '~/domains/design-system/icons/IconCircleCheck';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Routes } from '~/domains/common/constants/routes';
import { CurrencyConvertor } from '~/components/utils/formatters';

const REDIRECT_TIMEOUT = 5_000;

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 300,
    minWidth: '500px',

    [theme.fn.smallerThan('sm')]: {
      minWidth: '100%',
    },
  },
}));

type ReviveEntrySuccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  contestId: string;
  balanceInDollars: number;
  revivalsRemaining: number;
};

function ReviveEntrySuccessModal({
  isOpen,
  onClose,
  contestId,
  balanceInDollars,
  revivalsRemaining,
}: ReviveEntrySuccessModalProps) {
  const { t } = useTranslation('contest');
  const { classes, theme } = useStyles();
  const router = useRouter();
  const [remaining, setRemaining] = useState(REDIRECT_TIMEOUT);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        const nextValue = prev - 1000;
        if (nextValue > 0) {
          return nextValue;
        }

        clearInterval(timerRef.current);
        router.push(Routes.gamecenterStandingsOverall(contestId));
        onClose();

        return 0;
      });
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
    };
  }, [contestId, onClose, router]);

  return (
    <>
      {isOpen && <Confetti recycle={false} />}
      <Dialog type={{ desktop: 'modal', mobile: 'modal-full-screen' }} isOpen={isOpen}>
        <DialogBody className={classes.container}>
          <IconCircleCheck color={theme.colors.green[3]} />

          <Title variant="headline-xl" color={theme.colors.gray2[7]}>
            {t('common.reviveEntryModal.success.title')}
          </Title>

          <Text variant="body-large" color={theme.colors.gray2[7]}>
            {revivalsRemaining === 0
              ? t('common.reviveEntryModal.success.noMoreRevivals')
              : t('common.reviveEntryModal.success.revivalsRemaining', {
                  count: revivalsRemaining,
                })}
          </Text>

          <Text variant="body-large" color={theme.colors.gray2[4]} mt={theme.other.spacing._8}>
            <Trans
              i18nKey="contest:common.reviveEntryModal.success.balance"
              values={{
                balance: CurrencyConvertor(balanceInDollars),
              }}
              components={{
                balance: (
                  <Text
                    variant="headline-large"
                    color={theme.colors.gray2[7]}
                    mt={theme.other.spacing._8}
                  />
                ),
              }}
            />
          </Text>

          <Text variant="title-small" color={theme.colors.gray2[3]} mt={theme.other.spacing._16}>
            {t('common.enterContestModal.success.redirecting', {
              seconds: remaining / 1000,
            })}
          </Text>
        </DialogBody>
      </Dialog>
    </>
  );
}

export default ReviveEntrySuccessModal;
